import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Pencil, Trash2, Building2, DollarSign, TrendingUp, Star, Activity, FileText, Calendar, Paperclip, BarChart3, Brain } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { cn } from "@/lib/utils";
import AcquisitionCandidateForm from "@/components/acquisitions/AcquisitionCandidateForm";
import ScorecardSection from "@/components/acquisitions/ScorecardSection";
import ActivityTimeline from "@/components/acquisitions/ActivityTimeline";
import FinancialOverview from "@/components/acquisitions/FinancialOverview";
import FinancialTrendAnalysis from "@/components/acquisitions/FinancialTrendAnalysis";
import DocumentUpload from "@/components/collaboration/DocumentUpload";
import AIAnalysis from "@/components/acquisitions/AIAnalysis";
import ScheduleMeetingDialog from "@/components/acquisitions/ScheduleMeetingDialog";

const statusColors = {
  prospect: "bg-slate-100 text-slate-700",
  initial_contact: "bg-blue-100 text-blue-700",
  interested: "bg-cyan-100 text-cyan-700",
  info_sent: "bg-lime-100 text-zinc-900",
  evaluation: "bg-lime-100 text-zinc-900",
  letter_of_intent: "bg-amber-100 text-amber-700",
  negotiation_dd: "bg-orange-100 text-orange-700",
  closed_won: "bg-emerald-100 text-emerald-700",
};

const statusLabels = {
  prospect: "Prospekt",
  initial_contact: "Innledende Kontakt",
  interested: "Noe Interessert",
  info_sent: "Sendt Info og Følger Opp",
  evaluation: "Evaluering",
  letter_of_intent: "Intensjonsavtale",
  negotiation_dd: "Forhandling/DD",
  closed_won: "Gjennomført",
};

export default function AcquisitionCandidateDetail() {
  const [candidateId, setCandidateId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    setCandidateId(id);
  }, []);

  const { data: candidate } = useQuery({
    queryKey: ["acquisition-candidate", candidateId],
    queryFn: async () => {
      const candidates = await base44.entities.AcquisitionCandidate.filter({ id: candidateId });
      return candidates[0];
    },
    enabled: !!candidateId,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["acquisition-activities", candidateId],
    queryFn: () => base44.entities.AcquisitionActivity.filter({ candidate_id: candidateId }, "-date"),
    enabled: !!candidateId,
  });

  const updateCandidateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.AcquisitionCandidate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["acquisition-candidate"] });
      queryClient.invalidateQueries({ queryKey: ["acquisition-candidates"] });
      setShowForm(false);
    },
  });

  const deleteCandidateMutation = useMutation({
    mutationFn: (id) => base44.entities.AcquisitionCandidate.delete(id),
    onSuccess: () => {
      navigate(createPageUrl("AcquisitionCandidates"));
    },
  });

  if (!candidate) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(createPageUrl("AcquisitionCandidates"))}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Tilbake til Oversikt
        </Button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-zinc-900" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">{candidate.company_name}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className={cn("border-0", statusColors[candidate.status])}>
                    {statusLabels[candidate.status]}
                  </Badge>
                  {candidate.overall_score && (
                    <div className="flex items-center gap-1 px-3 py-1 bg-amber-50 rounded-lg">
                      <Star className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-900">
                        Score: {candidate.overall_score}/100
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowScheduleMeeting(true)}>
                <Calendar className="w-4 h-4 mr-2" />
                Planlegg Møte
              </Button>
              <Button variant="outline" size="icon" onClick={() => setShowForm(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="text-rose-600 hover:text-rose-700"
                onClick={() => {
                  if (confirm("Er du sikker på at du vil slette denne kandidaten?")) {
                    deleteCandidateMutation.mutate(candidateId);
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Kontaktperson</p>
                <p className="font-medium text-slate-900">{candidate.contact_person}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pris Diskutert</p>
                <p className="font-medium text-slate-900">
                  {candidate.discussed_price ? `${candidate.discussed_price.toLocaleString()} kr` : "Ikke satt"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">EBITDA Multipel</p>
                <p className="font-medium text-slate-900">
                  {candidate.multiple ? `${candidate.multiple}x` : "Ikke beregnet"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">
              <FileText className="w-4 h-4 mr-2" />
              Oversikt
            </TabsTrigger>
            <TabsTrigger value="scorecard">
              <Star className="w-4 h-4 mr-2" />
              Scorecard
            </TabsTrigger>
            <TabsTrigger value="activities">
              <Activity className="w-4 h-4 mr-2" />
              Aktiviteter
            </TabsTrigger>
            <TabsTrigger value="financial">
              <DollarSign className="w-4 h-4 mr-2" />
              Finansiell Info
            </TabsTrigger>
            <TabsTrigger value="analysis">
              <BarChart3 className="w-4 h-4 mr-2" />
              Trendanalyse
            </TabsTrigger>
            <TabsTrigger value="ai">
              <Brain className="w-4 h-4 mr-2" />
              AI-Analyse
            </TabsTrigger>
            <TabsTrigger value="documents">
              <Paperclip className="w-4 h-4 mr-2" />
              Dokumenter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6 bg-white border-0 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4">Kontaktinformasjon</h3>
                <div className="space-y-3">
                  {candidate.customer_name && (
                    <div>
                      <p className="text-sm text-slate-500">Relatert kunde</p>
                      <p className="font-medium text-zinc-900">{candidate.customer_name}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500">Kontaktperson</p>
                    <p className="font-medium text-slate-900">{candidate.contact_person}</p>
                  </div>
                  {candidate.contact_email && (
                    <div>
                      <p className="text-sm text-slate-500">E-post</p>
                      <p className="font-medium text-slate-900">{candidate.contact_email}</p>
                    </div>
                  )}
                  {candidate.contact_phone && (
                    <div>
                      <p className="text-sm text-slate-500">Telefon</p>
                      <p className="font-medium text-slate-900">{candidate.contact_phone}</p>
                    </div>
                  )}
                  {candidate.responsible_person_name && (
                    <div>
                      <p className="text-sm text-slate-500">Ansvarlig</p>
                      <p className="font-medium text-slate-900">{candidate.responsible_person_name}</p>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-6 bg-white border-0 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4">Selskapsinformasjon</h3>
                <div className="space-y-3">
                  {candidate.industry && (
                    <div>
                      <p className="text-sm text-slate-500">Bransje</p>
                      <p className="font-medium text-slate-900">{candidate.industry}</p>
                    </div>
                  )}
                  {candidate.employees && (
                    <div>
                      <p className="text-sm text-slate-500">Antall Ansatte</p>
                      <p className="font-medium text-slate-900">{candidate.employees}</p>
                    </div>
                  )}
                  {candidate.revenue && (
                    <div>
                      <p className="text-sm text-slate-500">Omsetning</p>
                      <p className="font-medium text-slate-900">{candidate.revenue.toLocaleString()} kr</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {candidate.notes && (
              <Card className="p-6 bg-white border-0 shadow-sm mt-6">
                <h3 className="font-semibold text-slate-900 mb-4">Notater</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{candidate.notes}</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="scorecard" className="mt-6">
            <ScorecardSection candidate={candidate} candidateId={candidateId} />
          </TabsContent>

          <TabsContent value="activities" className="mt-6">
            <ActivityTimeline candidateId={candidateId} candidateName={candidate.company_name} activities={activities} />
          </TabsContent>

          <TabsContent value="financial" className="mt-6">
            <FinancialOverview candidate={candidate} activities={activities} />
          </TabsContent>

          <TabsContent value="analysis" className="mt-6">
            <FinancialTrendAnalysis candidate={candidate} activities={activities} />
          </TabsContent>

          <TabsContent value="ai" className="mt-6">
            <AIAnalysis candidate={candidate} candidateId={candidateId} activities={activities} />
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <DocumentUpload
              relatedToType="acquisition_candidate"
              relatedToId={candidateId}
              relatedToName={candidate.company_name}
            />
          </TabsContent>
        </Tabs>
      </div>

      {showForm && (
        <AcquisitionCandidateForm
          candidate={candidate}
          onSubmit={(data) => updateCandidateMutation.mutate({ id: candidateId, data })}
          onClose={() => setShowForm(false)}
          isLoading={updateCandidateMutation.isPending}
        />
      )}

      {showScheduleMeeting && (
        <ScheduleMeetingDialog
          candidateId={candidateId}
          candidateName={candidate.company_name}
          onClose={() => setShowScheduleMeeting(false)}
        />
      )}
    </div>
  );
}